import { apiFetch } from '@/lib/api';

export interface PlatformAIConfig {
  id: string;
  provider: string;
  apiKeyHint: string;
  modelPreference: string | null;
  /** Complex-tier model. Null = Provider Default. */
  modelComplex: string | null;
  baseUrl: string | null;
  enabled: boolean;
  monthlyBudget: number | null;
  currentSpend: number;
  spendResetAt: string | null;
  capabilities: { text: boolean; vision: boolean; structuredOutput: boolean; embeddings: boolean; webSearch: boolean };
  createdAt: string;
  updatedAt: string;
}

export interface PlatformAIUsageSummary {
  provider: string;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
}

export interface PlatformAIConfigPayload {
  provider: string;
  apiKey: string;
  modelPreference?: string | null;
  modelComplex?: string | null;
  baseUrl?: string | null;
  enabled?: boolean;
  monthlyBudget?: number | null;
}

export interface PlatformAIConfigUpdatePayload {
  apiKey?: string;
  modelPreference?: string | null;
  modelComplex?: string | null;
  baseUrl?: string | null;
  enabled?: boolean;
  monthlyBudget?: number | null;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error?.message || `Request failed with status ${response.status}`);
  }
  return data.data;
}

export class PlatformAIConfigService {
  /** List all platform AI configs */
  async listConfigs(): Promise<PlatformAIConfig[]> {
    const response = await apiFetch('/api/admin/ai-config');
    return parseResponse<PlatformAIConfig[]>(response);
  }

  /** Create or upsert a platform provider config */
  async createConfig(payload: PlatformAIConfigPayload): Promise<PlatformAIConfig> {
    const response = await apiFetch('/api/admin/ai-config', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return parseResponse<PlatformAIConfig>(response);
  }

  /** Update a platform provider config */
  async updateConfig(id: string, payload: PlatformAIConfigUpdatePayload): Promise<PlatformAIConfig> {
    const response = await apiFetch(`/api/admin/ai-config/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return parseResponse<PlatformAIConfig>(response);
  }

  /** Soft-delete a platform provider config */
  async deleteConfig(id: string): Promise<void> {
    const response = await apiFetch(`/api/admin/ai-config/${id}`, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error?.message || 'Failed to delete config');
    }
  }

  /** Test connection for a platform provider config */
  async testConnection(id: string): Promise<{ success: boolean; message: string; model?: string }> {
    const response = await apiFetch(`/api/admin/ai-config/${id}/test`, {
      method: 'POST',
    });
    return parseResponse<{ success: boolean; message: string; model?: string }>(response);
  }

  /** Get platform usage summary */
  async getUsageSummary(days: number = 30): Promise<PlatformAIUsageSummary[]> {
    const response = await apiFetch(`/api/admin/ai-usage?days=${days}`);
    return parseResponse<PlatformAIUsageSummary[]>(response);
  }
}

export const platformAIConfigService = new PlatformAIConfigService();
